// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { CrmSyncStatus } from '@/constants/crm-sync-status';
import { FulfillmentStatus } from '@/constants/fulfillment-status';
import { ProjectCompanyType } from '@/constants/project-company-type';
import { ProjectEndClassification } from '@/constants/project-end-classification';
import { ProjectStartClassification } from '@/constants/project-start-classification';
import { ProjectStatusSlug, toProjectStatus } from '@/constants/project-status';
import { Sort } from '@/constants/sort';
import { projectSchema } from '@/project/api/types';
import { zodCatch } from '@/utilities/zod-catch';
import queryString from 'query-string';
import { z } from 'zod';

export const projectSortPropertySchema = z.enum([
  'name',
  'start',
  'end',
  'workflow',
]);

export type ProjectSortProperty = z.infer<typeof projectSortPropertySchema>;

/**
 * The name the project list sorts by on the wire, for each sort the app offers.
 */
const SORT_NAME_BY_PROPERTY: Record<ProjectSortProperty, string> = {
  name: 'name',
  start: 'startOn',
  end: 'endOn',
  workflow: 'workflow',
};

const PATH = '/v1/project';

const responseSchema = z
  .object({
    data: z.object({
      results: z.array(projectSchema).catch(zodCatch([])),
      totalCount: z.number(),
    }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    projects: data.results,
    totalCount: data.totalCount,
  }));

export interface ProjectFilter {
  query?: string;
  isArchived?: boolean | 'all';
  productFulfillmentStatus?: FulfillmentStatus[];
  projectFulfillmentStatus?: FulfillmentStatus[];
  productIds?: string[];
  projectIds?: string[];
  companyTypes?: ProjectCompanyType[];
  customerIds?: string[];
  locationIds?: string[];
  workflowStageIds?: string[];
  statuses?: ProjectStatusSlug[];
  syncStatuses?: CrmSyncStatus[];
  startBy?: string;
  startClassifications?: ProjectStartClassification[];
  endBy?: string;
  endClassifications?: ProjectEndClassification[];
  pageSize?: number;
  pageNumber?: number;
  sort?: Sort<ProjectSortProperty>;

  // Hidden parameters for timeline optimization
  completeDatesOnly?: boolean; // Filter to only projects with both start and end dates
  allRecords?: boolean; // Load all records without pagination for timeline view
}

/**
 * Requests all projects from the remote API.
 *
 * @param filter.isArchived - If true, only archived projects will be
 * requested. If false, only active projects will be requested.
 * @param filter.productIds - If provided, only projects associated with a
 * product represented by an ID in this list will be requested.
 * @param filter.customerIds - If provided, only projects associated with a
 * customer represented by an ID in this list will be requested.
 * @param filter.workflowStageIds - If provided, only projects associated with a
 * workflow stage represented by an ID in this list will be requested.
 * @param filter.projectIds - If provided, filters only for these selected projects
 * @param filter.locationIds - If provided, only projects associated with a
 * location represented by an ID in this list will be requested.
 * @param filter.statuses - If provided, only projects with a status that
 * matches one of the provided statuses will be requested.
 * @param filter.startBy - If provided, only projects that start on or before
 * the provided date will be requested.
 * @param filter.startClassifications - If provided, only projects with a start
 * classification that matches one of the provided classifications will be
 * requested.
 * @param filter.endBy - If provided, only projects that end on or before the
 * provided date will be requested.
 * @param filter.endClassifications - If provided, only projects with an end
 * classification that matches one of the provided classifications will be
 * requested.
 * @param filter.query - If provided, only projects with a name, description, customer,
 * or location that matches the query will be requested (handled server-side).
 */
export async function getProjects(filter?: ProjectFilter) {
  const response = await api.get(PATH, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      archived: filter?.isArchived,
      productFulfillmentStatus: filter?.productFulfillmentStatus,
      projectFulfillmentStatus: filter?.projectFulfillmentStatus,
      projectIds: filter?.projectIds,
      statuses: filter?.statuses?.map(toProjectStatus),
      syncStatuses: filter?.syncStatuses,
      itemIds: filter?.productIds,
      locationIds: filter?.locationIds,
      companyTypes: filter?.companyTypes,
      customerIds: filter?.customerIds,
      workflowStageIds: filter?.workflowStageIds,
      startBy: filter?.startBy,
      startClassifications: filter?.startClassifications,
      endBy: filter?.endBy,
      completionClassifications: filter?.endClassifications,
      limit: filter?.pageSize,
      page: filter?.pageNumber,
      sortBy: filter?.sort
        ? SORT_NAME_BY_PROPERTY[filter.sort.property]
        : undefined,
      sortOrder: filter?.sort?.direction ?? 'asc',
      completeDatesOnly: filter?.completeDatesOnly,
      allRecords: filter?.allRecords,
      search: filter?.query, // Pass query to backend as 'search' parameter
    },
  });

  const { projects, totalCount, ...result } = await parseResponse(
    responseSchema,
    response.data,
    {
      schemaName: 'Projects',
      endpoint: PATH,
    }
  );

  return {
    ...result,
    projects,
    totalCount,
  };
}
